import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
  type ReactNode,
} from 'react';
import type {
  BoardPoint,
  GameReviewFrame,
  StoneColor,
  TimedGameSnapshot,
} from '@wx-weiqi/contracts';
import type { StudentActivityApi } from '../services/student-activity';
import { secureRandomUuid } from '../services/random-id';
import {
  createClockProjectionAnchor,
  formatDuration,
  projectedByoyomi,
  projectedRemainingMs,
  projectedServerNow,
} from '../domain/clock-projection';
import { PlayerBar, useMonotonicNow } from '../App';
import { GoBoard } from './GoBoard';
import { BoardSurface } from './BoardSurface';
import { TeacherObservationBoardFrame } from './TeacherObservationBoardFrame';
import { TeacherObservationTrendChart } from './TeacherObservationTrendChart';
import { StudentRoomMembersCard, useStudentPolling } from './StudentPeopleCards';
import type { GameReplayStore } from '../stores/game-replay-store';
import { useObservationAi } from './use-observation-ai';
import { useObservationTimeline } from './use-observation-timeline';
import {
  pinObservationResearchBase,
  observationResearchBaseMatches,
  type ObservationResearchBase,
  type ObservationWinRatePoint,
} from '../domain/observation-analysis';
import {
  presentAiCandidates,
  formatAiVisits,
  formatAiScoreLead,
} from '../domain/ai-candidate-presentation';
import { boardPointLabel } from '../domain/game-interaction';
import { recentReviewTimingFrames } from '../domain/game-review-presentation';
import { formatPreciseDuration } from '../domain/timing-report';
import { branchResearchVariation, researchVariationPrefix } from '../domain/fox-kifu-presentation';
import './teacher-observation.css';

interface ObservationView {
  readonly snapshot: TimedGameSnapshot;
  readonly frames?: readonly GameReviewFrame[] | undefined;
}

interface TeacherObservationViewProps {
  readonly pageRef: RefObject<HTMLElement | null>;
  readonly immersive: boolean;
  readonly toggleFullscreen: () => void;
  readonly onBack: () => void;
  readonly onSwitchGame: () => void;
  readonly gamePicker: ReactNode;
  readonly snapshot: TimedGameSnapshot | undefined;
  readonly frame: GameReviewFrame | undefined;
  readonly frames: readonly GameReviewFrame[] | undefined;
  readonly cursor: number;
  readonly lastFrameIndex: number;
  readonly followingLive: boolean;
  readonly selectMove: (move: number) => void;
  readonly stateError: string | null;
  readonly pending: boolean;
  readonly now: number;
  readonly anchor: ReturnType<typeof createClockProjectionAnchor> | null;
  readonly boardStones: readonly (StoneColor | null)[];
  readonly variationLastMove: BoardPoint | null;
  readonly researching: boolean;
  readonly researchCursor: number;
  readonly variationLength: number;
  readonly researchPending: boolean;
  readonly researchError: string | null;
  readonly retryResearch: (() => void) | null;
  readonly placeResearch: (point: BoardPoint) => void;
  readonly navigateResearch: (cursor: number) => void;
  readonly analyticsTab: 'TIMING' | 'WIN_RATE';
  readonly setAnalyticsTab: (tab: 'TIMING' | 'WIN_RATE') => void;
  readonly aiEnabled: boolean;
  readonly aiPending: boolean;
  readonly aiError: string | null;
  readonly retryAi: () => void;
  readonly timeline: ReturnType<typeof useObservationTimeline>;
  readonly winRates: readonly ObservationWinRatePoint[];
  readonly aiCandidates: readonly ReturnType<typeof presentAiCandidates>[number][];
  readonly toggleAi: () => void;
  readonly toggleResearch: () => void;
}

function TeacherObservationView({
  pageRef,
  immersive,
  toggleFullscreen,
  onBack,
  onSwitchGame,
  gamePicker,
  snapshot,
  frame,
  frames,
  cursor,
  lastFrameIndex,
  followingLive,
  selectMove,
  stateError,
  pending,
  now,
  anchor,
  boardStones,
  variationLastMove,
  researching,
  researchCursor,
  variationLength,
  researchPending,
  researchError,
  retryResearch,
  placeResearch,
  navigateResearch,
  analyticsTab,
  setAnalyticsTab,
  aiEnabled,
  aiPending,
  aiError,
  retryAi,
  winRates,
  timeline,
  aiCandidates,
  toggleAi,
  toggleResearch,
}: TeacherObservationViewProps) {
  const recentFrames = useMemo(
    () => recentReviewTimingFrames(frames ?? [], cursor, 5),
    [cursor, frames],
  );
  const timingValues = recentFrames.map((item) => item.elapsedMs ?? 0);
  const maxTiming = Math.max(1, ...timingValues);
  const players = snapshot?.room.players ?? [];
  const black = players.find((player) => player.seat === 'BLACK');
  const white = players.find((player) => player.seat === 'WHITE');
  const lastPoint = variationLastMove ?? frame?.point ?? null;
  const lastStone =
    snapshot !== undefined && lastPoint !== null
      ? boardStones[lastPoint.y * snapshot.board.size + lastPoint.x]
      : null;
  const moveNumber = (frame?.moveNumber ?? cursor) + (researching ? researchCursor : 0);
  const lastMoveLabels =
    lastPoint !== null && lastStone != null && moveNumber > 0
      ? [{ point: lastPoint, color: lastStone, label: String(moveNumber) }]
      : [];

  return (
    <main
      ref={pageRef}
      aria-label="实时观战页面"
      className={`board-workspace student-play-workspace game-page student-observation-page student-observation-page--teacher${immersive ? ' game-page--immersive' : ''}`}
    >
      <section className="teacher-observation-grid">
        <aside className="teacher-observation-left" aria-label="对局信息">
          <section className="teacher-observation-player-card">
            <span className="teacher-observation-eyebrow">黑方</span>
            <div className="teacher-observation-player-name">
              <i className="teacher-observation-stone teacher-observation-stone--black" />
              <strong>{black?.displayName ?? '等待入座'}</strong>
            </div>
            <time>
              {snapshot !== undefined && anchor !== null
                ? playerClock(snapshot, anchor, now, 'BLACK')
                : '—'}
            </time>
            <small>{activePlayerLabel(snapshot, 'BLACK')}</small>
          </section>
          <section className="teacher-observation-player-card teacher-observation-player-card--white">
            <span className="teacher-observation-eyebrow">白方</span>
            <div className="teacher-observation-player-name">
              <i className="teacher-observation-stone teacher-observation-stone--white" />
              <strong>{white?.displayName ?? '等待入座'}</strong>
            </div>
            <time>
              {snapshot !== undefined && anchor !== null
                ? playerClock(snapshot, anchor, now, 'WHITE')
                : '—'}
            </time>
            <small>{activePlayerLabel(snapshot, 'WHITE')}</small>
          </section>
          <section className="teacher-observation-status-card">
            <span>当前进度</span>
            <strong>{snapshot === undefined ? '—' : `第 ${cursor} 手`}</strong>
            <small>
              {researching ? '正在研究变化' : followingLive ? '实时同步中' : '正在回看历史局面'} ·{' '}
              {snapshot?.board.size ?? 19} 路
            </small>
          </section>
          <nav className="teacher-observation-left-actions" aria-label="观战操作">
            {(!followingLive || researching) && (
              <button
                className="teacher-observation-return-live"
                type="button"
                onClick={() => selectMove(lastFrameIndex)}
              >
                回到实战 · 第 {lastFrameIndex} 手
              </button>
            )}
            <button type="button" onClick={onSwitchGame}>
              切换对局
            </button>
            <button className="fullscreen-action" type="button" onClick={toggleFullscreen}>
              {immersive ? '退出全屏' : '进入全屏'}
            </button>
            <button type="button" onClick={onBack}>
              返回大厅
            </button>
          </nav>
        </aside>

        <section className="teacher-observation-board-column" aria-label="学生对局棋盘">
          {snapshot === undefined ? (
            <p role="status">{stateError ?? '正在加载棋局…'}</p>
          ) : (
            <TeacherObservationBoardFrame>
              <BoardSurface
                boardAppearance="wood"
                stoneAppearance="shell"
                size={snapshot.board.size}
                stones={boardStones}
                lastMove={lastMoveLabels.length > 0 ? null : lastPoint}
                stoneLabels={lastMoveLabels}
                aiCandidates={aiCandidates.slice(0, 5)}
                {...(researching
                  ? {
                      isActionable: (_point: BoardPoint, stone: StoneColor | null) =>
                        stone === null,
                      onPoint: placeResearch,
                    }
                  : {})}
              />
            </TeacherObservationBoardFrame>
          )}
        </section>

        <aside className="teacher-observation-right" aria-label="对局分析">
          <section className="teacher-observation-ai-card">
            <header>
              <div>
                <span className="teacher-observation-eyebrow">AI辅助</span>
                <h2>AI推荐点</h2>
              </div>
              <button type="button" className={aiEnabled ? 'is-active' : ''} onClick={toggleAi}>
                {aiPending ? '分析中…' : aiEnabled ? '停止分析' : 'AI分析'}
              </button>
            </header>
            {snapshot !== undefined && (
              <p className="teacher-observation-ai-rules">
                {snapshot.room.handicap === 0
                  ? '分先'
                  : snapshot.room.handicap === 1
                    ? '让先'
                    : `让 ${snapshot.room.handicap} 子`}
                {' · 中国规则 · 白补偿 '}
                {snapshot.room.komi} 目
              </p>
            )}
            {aiError ? (
              <p className="teacher-observation-error" role="alert">
                {aiError}
                <button type="button" onClick={retryAi} disabled={aiPending}>
                  重试分析
                </button>
              </p>
            ) : null}
            {aiCandidates.length === 0 ? (
              <p className="teacher-observation-ai-empty">
                {aiPending
                  ? '正在分析当前局面…'
                  : aiEnabled
                    ? researchPending
                      ? '正在确认研究局面，完成后继续分析…'
                      : researchError
                        ? '请先恢复有效研究局面'
                        : '等待当前局面的分析结果'
                    : '点击“AI分析”后显示推荐点'}
              </p>
            ) : (
              <div className="teacher-observation-ai-table" role="table" aria-label="AI推荐点">
                <div
                  className="teacher-observation-ai-row teacher-observation-ai-row--head"
                  role="row"
                >
                  <span>落点</span>
                  <span title="当前行棋方领先的目数；负数表示落后">目差</span>
                  <span title="当前行棋方胜率">胜率</span>
                  <span>计算量</span>
                </div>
                {aiCandidates.slice(0, 5).map((candidate) => (
                  <div
                    className="teacher-observation-ai-row"
                    role="row"
                    key={`${candidate.rank}-${candidate.point.x}-${candidate.point.y}`}
                  >
                    <strong>
                      {candidate.rank}.{' '}
                      {snapshot === undefined
                        ? '—'
                        : boardPointLabel(candidate.point, snapshot.board.size)}
                    </strong>
                    <span>
                      {candidate.scoreLeadHalfPoints > 0 ? '+' : ''}
                      {formatAiScoreLead(candidate.scoreLeadHalfPoints)}
                    </span>
                    <span>{(candidate.winRateBps / 100).toFixed(1)}%</span>
                    <span>{formatAiVisits(candidate.visits)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="teacher-observation-analytics-card">
            <div className="teacher-observation-tabs" role="tablist" aria-label="对局分析视图">
              <button
                type="button"
                role="tab"
                aria-selected={analyticsTab === 'TIMING'}
                onClick={() => setAnalyticsTab('TIMING')}
              >
                最近逐手用时
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={analyticsTab === 'WIN_RATE'}
                onClick={() => setAnalyticsTab('WIN_RATE')}
              >
                胜率走势图
              </button>
            </div>
            {analyticsTab === 'WIN_RATE' && (aiEnabled || timeline.points.length > 0) && (
              <div className="teacher-observation-history-status" role="status">
                <span>
                  {aiEnabled
                    ? timeline.data === null
                      ? '正在加载历史胜率…'
                      : timeline.data.completed >= timeline.data.total
                        ? '历史胜率已补齐'
                        : `正在补算 · ${timeline.data.completed} / ${timeline.data.total} 个局面`
                    : '补算已暂停，开启 AI 后继续'}
                </span>
                {timeline.error !== null && (
                  <span>
                    {timeline.error}{' '}
                    <button type="button" onClick={timeline.retry} disabled={!aiEnabled}>
                      重试补算
                    </button>
                  </span>
                )}
              </div>
            )}
            {analyticsTab === 'TIMING' ? (
              <section className="teacher-observation-timing" aria-label="最近逐手用时">
                {recentFrames.length === 0 ? (
                  <p className="teacher-observation-ai-empty">前进棋谱后显示最近 5 手用时。</p>
                ) : (
                  <ol>
                    {recentFrames.map((item) => (
                      <li
                        key={item.moveNumber}
                        className={item.color === 'WHITE' ? 'is-white' : ''}
                      >
                        <span>
                          {item.color === 'BLACK' ? '黑' : '白'} · 第 {item.moveNumber} 手
                        </span>
                        <i
                          style={{
                            width: `${Math.max(8, ((item.elapsedMs ?? 0) / maxTiming) * 100)}%`,
                          }}
                        />
                        <strong>{formatPreciseDuration(item.elapsedMs)}</strong>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            ) : (
              <TeacherObservationTrendChart
                points={winRates}
                currentMove={cursor}
                lastMoveNumber={lastFrameIndex}
                onSelectMove={selectMove}
                aiEnabled={aiEnabled}
                aiPending={aiPending}
              />
            )}
          </section>

          {researchError ? (
            <div className="teacher-observation-error" role="alert">
              {researchError}
              {retryResearch !== null ? (
                <button type="button" onClick={retryResearch} disabled={researchPending}>
                  重试研究
                </button>
              ) : null}
            </div>
          ) : null}
          <section className="teacher-observation-toolbar" aria-label="观战工具栏">
            <button
              type="button"
              disabled={
                researching ? researchCursor === 0 || researchPending : cursor === 0 || pending
              }
              onClick={() => (researching ? navigateResearch(0) : selectMove(0))}
              aria-label="首手"
            >
              «
            </button>
            <button
              type="button"
              disabled={
                researching ? researchCursor === 0 || researchPending : cursor === 0 || pending
              }
              onClick={() =>
                researching ? navigateResearch(researchCursor - 1) : selectMove(cursor - 1)
              }
              aria-label="上一手"
            >
              ‹
            </button>
            <span aria-live="polite">
              {researching ? `变化第 ${researchCursor} 手` : `第 ${cursor} 手`}
            </span>
            <button
              type="button"
              disabled={
                researching
                  ? researchCursor >= variationLength || researchPending
                  : cursor >= lastFrameIndex || pending
              }
              onClick={() =>
                researching ? navigateResearch(researchCursor + 1) : selectMove(cursor + 1)
              }
              aria-label="下一手"
            >
              ›
            </button>
            <button
              type="button"
              disabled={
                researching
                  ? researchCursor >= variationLength || researchPending
                  : cursor >= lastFrameIndex || pending
              }
              onClick={() =>
                researching ? navigateResearch(variationLength) : selectMove(lastFrameIndex)
              }
              aria-label="末手"
            >
              »
            </button>
            <button
              type="button"
              className={researching ? 'is-active' : ''}
              onClick={toggleResearch}
            >
              {researching ? '结束研究' : '研究'}
            </button>
            <input
              type="range"
              min={0}
              max={researching ? variationLength : lastFrameIndex}
              value={researching ? researchCursor : cursor}
              disabled={pending || (researching && researchPending)}
              aria-label="观战手数"
              onChange={(event) => {
                const next = Number(event.currentTarget.value);
                if (researching) navigateResearch(next);
                else selectMove(next);
              }}
            />
          </section>
        </aside>
      </section>
      {gamePicker}
    </main>
  );
}

function playerClock(
  snapshot: TimedGameSnapshot,
  anchor: ReturnType<typeof createClockProjectionAnchor>,
  now: number,
  color: StoneColor,
) {
  const serverNow = projectedServerNow(anchor, now);
  const byoyomi = projectedByoyomi(snapshot.timing, color, serverNow);
  if (byoyomi !== null)
    return `${Math.ceil(byoyomi.periodRemainingMs / 1_000)}s · ${byoyomi.periods}次`;
  return formatDuration(projectedRemainingMs(snapshot.timing, color, serverNow));
}

function activePlayerLabel(snapshot: TimedGameSnapshot | undefined, color: StoneColor): string {
  if (snapshot === undefined) return '等待棋局数据';
  if (snapshot.status !== 'PLAYING')
    return snapshot.status === 'FINISHED' ? '对局已结束' : '未开始';
  return snapshot.board.currentPlayer === color ? '思考中' : '等待对手';
}

export function StudentWatchGamesPanel({
  api,
  onWatch,
}: {
  readonly api: StudentActivityApi;
  readonly onWatch: (gameId: string) => void;
}) {
  const [page, setPage] = useState(1);
  const load = useCallback(() => api.watchGames(page, 8), [api, page]);
  const state = useStudentPolling(load);
  const pages = Math.max(1, Math.ceil((state.data?.total ?? 0) / 8));
  useEffect(() => {
    if (state.data !== null && page > pages) setPage(pages);
  }, [state.data, page, pages]);
  return (
    <section className="student-watch-list" aria-label="可观战对局">
      <header>
        <p>同一棋院正在进行的对局 · 只读观战</p>
        <button type="button" onClick={state.refresh} disabled={state.pending}>
          刷新列表
        </button>
      </header>
      {state.error ? (
        <p className="message message--error" role="alert">
          {state.error}
        </p>
      ) : state.pending ? (
        <p>正在加载对局…</p>
      ) : state.data?.games.length === 0 ? (
        <p className="student-watch-empty">当前暂无可观战的对局。</p>
      ) : (
        <ul>
          {state.data?.games.map((game) => (
            <li key={game.gameId}>
              <div>
                <strong>
                  {game.players.find((player) => player.color === 'BLACK')?.displayName ?? '黑方'}{' '}
                  <small>对</small>{' '}
                  {game.players.find((player) => player.color === 'WHITE')?.displayName ?? '白方'}
                </strong>
                <span>
                  {game.boardSize} 路 · 第 {game.currentSequence} 手 ·{' '}
                  {game.status === 'PLAYING' ? '对弈中' : '数子中'}
                </span>
              </div>
              <button type="button" onClick={() => onWatch(game.gameId)}>
                观战
              </button>
            </li>
          ))}
        </ul>
      )}
      <footer>
        <button
          type="button"
          disabled={page <= 1 || state.pending}
          onClick={() => setPage((value) => value - 1)}
        >
          上一页
        </button>
        <span>
          {page} / {pages} 页 · 共 {state.data?.total ?? 0} 局
        </span>
        <button
          type="button"
          disabled={page >= pages || state.pending}
          onClick={() => setPage((value) => value + 1)}
        >
          下一页
        </button>
      </footer>
    </section>
  );
}

export function StudentObservationPage({
  api,
  gameId,
  onBack,
  onSwitchGame,
  gamePicker,
  readTeacherGame,
  replayStore,
}: {
  readonly api: StudentActivityApi;
  readonly gameId: string;
  readonly onBack: () => void;
  readonly onSwitchGame?: (() => void) | undefined;
  readonly gamePicker?: ReactNode;
  readonly readTeacherGame?: ((gameId: string) => Promise<ObservationView>) | undefined;
  readonly replayStore?: GameReplayStore | undefined;
}) {
  const [viewerId] = useState(secureRandomUuid);
  const [immersive, setImmersive] = useState(false);
  // null follows the live game; a chosen move remains pinned while students play on.
  const [reviewPosition, setReviewPosition] = useState<{ gameId: string; move: number } | null>(
    null,
  );
  const [analyticsTab, setAnalyticsTab] = useState<'TIMING' | 'WIN_RATE'>('TIMING');
  const [researchBase, setResearchBase] = useState<ObservationResearchBase | null>(null);
  const [failedResearch, setFailedResearch] = useState<{
    moves: readonly BoardPoint[];
    cursor: number;
  } | null>(null);
  const interactionGeneration = useRef(0);
  const [researchTransition, setResearchTransition] = useState(false);
  const [variationMoves, setVariationMoves] = useState<readonly BoardPoint[]>([]);
  const [researchCursor, setResearchCursor] = useState(0);
  const pageRef = useRef<HTMLElement>(null);
  const emptyReplaySubscribe = useCallback(() => () => undefined, []);
  const emptyReplaySnapshot = useCallback(() => null, []);
  const replayState = useSyncExternalStore(
    replayStore?.subscribe ?? emptyReplaySubscribe,
    replayStore?.snapshot ?? emptyReplaySnapshot,
    emptyReplaySnapshot,
  );
  const load = useCallback(
    (): Promise<ObservationView> =>
      readTeacherGame === undefined ? api.observe(gameId, viewerId) : readTeacherGame(gameId),
    [api, gameId, viewerId, readTeacherGame],
  );
  const state = useStudentPolling(load, 3000);
  const snapshot = state.data?.snapshot.gameId === gameId ? state.data.snapshot : undefined;
  const frames = snapshot === undefined ? undefined : state.data?.frames;
  const lastFrameIndex = Math.max(0, (frames?.length ?? 1) - 1);
  const followingLive = reviewPosition === null || reviewPosition.gameId !== gameId;
  const cursor = followingLive ? lastFrameIndex : Math.min(reviewPosition.move, lastFrameIndex);
  const frame = frames?.[cursor];
  const selectMove = (move: number) => {
    // The trend always addresses the real game timeline, including while trying a variation.
    if (researchBase !== null) {
      interactionGeneration.current += 1;
      setResearchBase(null);
      setResearchTransition(false);
      setFailedResearch(null);
      setVariationMoves([]);
      setResearchCursor(0);
      replayStore?.clearResearch();
    }
    setReviewPosition(move >= lastFrameIndex ? null : { gameId, move: Math.max(0, move) });
  };
  const now = useMonotonicNow();
  const anchor = useMemo(
    () =>
      snapshot === undefined
        ? null
        : createClockProjectionAnchor(snapshot.timing.asOf, performance.now()),
    [snapshot],
  );
  useEffect(
    () => () => {
      if (readTeacherGame === undefined)
        void api.leaveObservation(gameId, viewerId).catch(() => undefined);
    },
    [api, gameId, viewerId, readTeacherGame],
  );
  useEffect(() => {
    const sync = () => setImmersive(document.fullscreenElement === pageRef.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  const toggleFullscreen = async () => {
    if (document.fullscreenElement === pageRef.current) {
      await document.exitFullscreen();
      return;
    }
    if (immersive) {
      setImmersive(false);
      return;
    }
    try {
      await pageRef.current?.requestFullscreen();
    } catch {
      /* CSS fullscreen fallback. */
    }
    setImmersive(true);
  };
  const researching = readTeacherGame !== undefined && researchBase?.gameId === gameId;
  const baseFrame = researching ? researchBase.frame : frame;
  const baseMoveNumber = baseFrame?.moveNumber ?? cursor;
  const activeVariationMoves = useMemo(
    () => researchVariationPrefix(variationMoves, researchCursor),
    [researchCursor, variationMoves],
  );
  const researchBaseChanged =
    researching &&
    frames !== undefined &&
    snapshot !== undefined &&
    !observationResearchBaseMatches(researchBase, frames, snapshot.board.currentPlayer);
  const position =
    snapshot === undefined
      ? null
      : {
          gameId,
          stateVersion: snapshot.stateVersion,
          baseMoveNumber,
          moves: researching ? activeVariationMoves : [],
        };
  const ai = useObservationAi({
    gameId,
    position,
    suspended: researchBaseChanged || researchTransition || Boolean(replayState?.researchPending),
    replayStore,
    replayState,
  });
  const matchingAnalysis = ai.analysis;
  const aiCandidates = useMemo(() => presentAiCandidates(matchingAnalysis), [matchingAnalysis]);
  const timeline = useObservationTimeline(
    replayStore,
    snapshot !== undefined && frames !== undefined && readTeacherGame !== undefined
      ? {
          gameId,
          stateVersion: snapshot.stateVersion,
          frames,
          configurationKey: JSON.stringify([
            replayState?.aiSelectedConfigurationId,
            replayState?.aiProfile,
          ]),
          rulesKey: JSON.stringify([
            snapshot.board.size,
            snapshot.room.ruleset,
            snapshot.room.komi,
            snapshot.room.handicap,
          ]),
          currentPlayer: snapshot.board.currentPlayer,
        }
      : null,
    ai.enabled && replayState?.aiAvailable === true,
  );
  const winRates = timeline.points;
  const recordForeground = timeline.recordForeground;
  const teacherBoardStones = researching
    ? (replayState?.research?.stones ?? baseFrame?.stones ?? [])
    : (frame?.stones ?? snapshot?.board.stones ?? []);

  useEffect(() => {
    if (
      researching ||
      matchingAnalysis?.result === null ||
      matchingAnalysis === null ||
      frame === undefined
    )
      return;
    recordForeground(frame.moveNumber, matchingAnalysis);
  }, [matchingAnalysis, researching, gameId, frame, recordForeground]);

  useEffect(() => {
    interactionGeneration.current += 1;
    setReviewPosition(null);
    setAnalyticsTab('TIMING');
    setResearchBase(null);
    setVariationMoves([]);
    setResearchCursor(0);
    setFailedResearch(null);
    setResearchTransition(false);
    replayStore?.clearResearch();
    return () => {
      interactionGeneration.current += 1;
      replayStore?.clearResearch();
    };
  }, [gameId, replayStore]);

  const toggleResearch = () => {
    interactionGeneration.current += 1;
    setResearchTransition(false);
    replayStore?.clearResearch();
    setFailedResearch(null);
    setVariationMoves([]);
    setResearchCursor(0);
    if (researching) {
      setResearchBase(null);
      return;
    }
    if (frame === undefined || frames === undefined || snapshot === undefined) return;
    // Freeze the originating frame and timeline prefix while live polling continues for clocks.
    setReviewPosition({ gameId, move: cursor });
    setResearchBase(
      pinObservationResearchBase(gameId, frames, cursor, snapshot.board.currentPlayer),
    );
  };

  const runResearch = (moves: readonly BoardPoint[], target: number) => {
    if (
      !researching ||
      replayStore === undefined ||
      snapshot === undefined ||
      researchTransition ||
      replayState?.researchPending ||
      researchBaseChanged
    )
      return;
    const generation = ++interactionGeneration.current;
    setResearchTransition(true);
    setFailedResearch(null);
    void replayStore
      .researchObservation({
        gameId,
        stateVersion: snapshot.stateVersion,
        baseMoveNumber: researchBase.frame.moveNumber,
        moves: researchVariationPrefix(moves, target),
      })
      .then((applied) => {
        if (!applied || generation !== interactionGeneration.current) return;
        setVariationMoves(moves);
        setResearchCursor(target);
      })
      .catch(() => {
        if (generation === interactionGeneration.current)
          setFailedResearch({ moves, cursor: target });
      })
      .finally(() => {
        if (generation === interactionGeneration.current) setResearchTransition(false);
      });
  };

  const placeResearch = (point: BoardPoint) => {
    const next = branchResearchVariation(variationMoves, researchCursor, point);
    runResearch(next, next.length);
  };

  const navigateResearch = (targetCursor: number) => {
    const target = Math.max(0, Math.min(variationMoves.length, Math.trunc(targetCursor)));
    if (
      !researching ||
      target === researchCursor ||
      researchTransition ||
      replayState?.researchPending
    )
      return;
    if (target === 0) {
      interactionGeneration.current += 1;
      setResearchTransition(false);
      replayStore?.clearResearch();
      setFailedResearch(null);
      setResearchCursor(0);
      return;
    }
    runResearch(variationMoves, target);
  };

  const showTeacherObservation = readTeacherGame !== undefined;
  const leaveObservation = (navigate: () => void) => {
    interactionGeneration.current += 1;
    ai.stop();
    replayStore?.clearResearch();
    navigate();
  };
  if (showTeacherObservation) {
    return (
      <TeacherObservationView
        pageRef={pageRef}
        immersive={immersive}
        toggleFullscreen={() => void toggleFullscreen()}
        onBack={() => leaveObservation(onBack)}
        onSwitchGame={() => onSwitchGame?.()}
        gamePicker={gamePicker}
        snapshot={snapshot}
        frame={baseFrame}
        frames={frames}
        cursor={cursor}
        lastFrameIndex={lastFrameIndex}
        followingLive={followingLive}
        selectMove={selectMove}
        stateError={state.error}
        pending={state.pending}
        now={now}
        anchor={anchor}
        boardStones={teacherBoardStones}
        variationLastMove={
          researching && activeVariationMoves.length > 0
            ? (activeVariationMoves[activeVariationMoves.length - 1] ?? null)
            : null
        }
        researching={researching}
        researchCursor={researchCursor}
        variationLength={variationMoves.length}
        researchPending={researchTransition || Boolean(replayState?.researchPending)}
        researchError={
          researchBaseChanged
            ? '原研究局面已变更，请结束研究后重新开始。'
            : researching
              ? (replayState?.error ?? null)
              : null
        }
        retryResearch={
          failedResearch !== null && !researchBaseChanged
            ? () => runResearch(failedResearch.moves, failedResearch.cursor)
            : null
        }
        placeResearch={placeResearch}
        navigateResearch={navigateResearch}
        analyticsTab={analyticsTab}
        setAnalyticsTab={setAnalyticsTab}
        aiEnabled={ai.enabled}
        aiPending={ai.pending}
        aiError={ai.error}
        retryAi={ai.retry}
        winRates={winRates}
        timeline={timeline}
        aiCandidates={aiCandidates}
        toggleAi={() => void ai.toggle()}
        toggleResearch={toggleResearch}
      />
    );
  }
  return (
    <main
      ref={pageRef}
      className={`board-workspace student-play-workspace game-page student-observation-page${immersive ? ' game-page--immersive' : ''}`}
    >
      <section className="game-layout">
        <aside className="student-game-context">
          <section className="game-heading">
            <div className="game-heading-main">
              <button className="back-button return-button" type="button" onClick={onBack}>
                ← 返回对局列表
              </button>
              <h1>{readTeacherGame === undefined ? '观战' : '学生当前对局'}</h1>
              <p>只读观看，不参与落子和计时。</p>
            </div>
            <div className="game-heading-actions">
              <button
                className="fullscreen-button fullscreen-action"
                type="button"
                onClick={() => void toggleFullscreen()}
              >
                {immersive ? '退出全屏' : '进入全屏'}
              </button>
            </div>
          </section>
          <section className="move-summary">
            <span>当前手数</span>
            <strong>{snapshot?.currentSequence ?? '—'}</strong>
            <span>对局状态</span>
            <strong>
              {snapshot?.status === 'FINISHED'
                ? '已结束'
                : snapshot?.status === 'ABORTED'
                  ? '已中止'
                  : snapshot?.status === 'PLAYING'
                    ? '对弈中'
                    : '观战'}
            </strong>
          </section>
          <p className="connection-card">每 3 秒同步服务器局面</p>
        </aside>
        <div className="board-column">
          {snapshot === undefined ? (
            <p role="status">{state.error ?? '正在加载棋局…'}</p>
          ) : frame !== undefined && !followingLive ? (
            <BoardSurface size={snapshot.board.size} stones={frame.stones} lastMove={frame.point} />
          ) : (
            <GoBoard
              snapshot={snapshot}
              disabled
              scoringMode={false}
              scoringTargetColor={null}
              selectedDisputedStones={new Set()}
              lastMove={frame?.point ?? null}
              onPoint={() => undefined}
            />
          )}
        </div>
        <aside className="game-sidebar">
          {snapshot !== undefined && anchor !== null ? (
            <section className="player-panel" aria-label="对局双方">
              <PlayerBar color="WHITE" snapshot={snapshot} now={projectedServerNow(anchor, now)} />
              <PlayerBar color="BLACK" snapshot={snapshot} now={projectedServerNow(anchor, now)} />
            </section>
          ) : null}
          {readTeacherGame === undefined ? (
            <StudentRoomMembersCard api={api} gameId={gameId} />
          ) : null}
          <section className="action-card">
            <h2>{snapshot?.terminal?.finalResult ?? '正在观战'}</h2>
            <p>可查看双方落子和棋钟，不能操作本局。</p>
            <button type="button" onClick={state.refresh} disabled={state.pending}>
              刷新棋局
            </button>
            {state.error ? <p role="alert">{state.error}</p> : null}
          </section>
          {readTeacherGame !== undefined ? (
            <section className="observation-playback action-card" aria-label="观战进度">
              <header>
                <h2>{followingLive ? '实时局面' : '回看局面'}</h2>
                <strong>
                  第 {cursor} / {lastFrameIndex} 手
                </strong>
              </header>
              <p>
                {followingLive
                  ? '标记当前最后一手，每 3 秒同步。'
                  : '正在回看；棋钟和对局状态仍显示实时信息。'}
              </p>
              <input
                type="range"
                aria-label="观战手数"
                min={0}
                max={lastFrameIndex}
                value={cursor}
                disabled={frame === undefined || lastFrameIndex === 0}
                onChange={(event) => selectMove(Number(event.currentTarget.value))}
              />
              <div className="observation-playback__buttons">
                <button
                  type="button"
                  disabled={cursor === 0 || frame === undefined}
                  onClick={() => selectMove(0)}
                >
                  首手
                </button>
                <button
                  type="button"
                  disabled={cursor === 0 || frame === undefined}
                  onClick={() => selectMove(cursor - 1)}
                >
                  上一手
                </button>
                <button
                  type="button"
                  disabled={cursor >= lastFrameIndex || frame === undefined}
                  onClick={() => selectMove(cursor + 1)}
                >
                  下一手
                </button>
                <button
                  type="button"
                  className="primary-button"
                  disabled={followingLive}
                  onClick={() => setReviewPosition(null)}
                >
                  回到最新
                </button>
              </div>
              {frame === undefined && !state.pending ? (
                <p>当前服务暂未提供回放进度，请刷新后重试。</p>
              ) : null}
            </section>
          ) : null}
        </aside>
      </section>
    </main>
  );
}
